/**
 * Shared AI call helper. Every call routes through the Supabase edge
 * function `anthropic-proxy`, which holds the API key server-side. The
 * browser bundle never sees the key — there is no direct-to-Anthropic
 * path, no localStorage-key escape hatch.
 *
 * Retries + model selection live here on the client; the proxy stays a
 * thin forwarder. The edge function handles its own retry for transient
 * Anthropic errors.
 */

import { getModel } from './anthropicConfig';
import { getSupabaseClient, supabase } from './supabase';
import { getModelFor, recordCall, type SurfaceId } from './aiSurfaces';
import { buildCoachStyleHint, type CoachLevel, type CommsStyle } from './profiles';
import { debug, formatError } from '@4cm/shared';

const logAi = debug('ai');
const logJson = debug('ai:json');
const logSurface = debug('ai:surface');
const logTools = debug('ai:tools');

export type Effort = 'low' | 'medium' | 'high';

/** Effort → model tier. Short-circuits the surface-default lookup when
 *  the caller wants explicit control (e.g. Scratch Area's Low/Med/High
 *  selector). Lets a demo coach flip between Haiku for fast clustering
 *  and Opus for nuanced promote without touching the ModelOverridePanel. */
const EFFORT_MODEL: Record<Effort, string> = {
  low:    'claude-haiku-4-5-20251001',
  medium: 'claude-sonnet-4-6',
  high:   'claude-opus-4-7',
};

/** Resolve the model for a call. Precedence:
 *    1. `effort` (explicit tier from caller — Low/Med/High)
 *    2. `surface` (per-surface default + override panel)
 *    3. legacy `getModel()` */
function pickModel(surface: SurfaceId | undefined, effort?: Effort): string {
  if (effort) {
    const model = EFFORT_MODEL[effort];
    if (surface) recordCall(surface, model);
    logSurface.debug(surface ?? '<unscoped>', { model, effort });
    return model;
  }
  const model = surface ? getModelFor(surface) : getModel();
  if (surface) recordCall(surface, model);
  logSurface.debug(surface ?? '<unscoped>', { model });
  return model;
}

/** Coach context for AI generators. When `coachContext` is supplied
 *  on AICallOptions, callAI auto-prepends the coach style hint to
 *  the system prompt so the model's tone matches the coach's level
 *  + Brain rating without every surface remembering to call
 *  buildCoachStyleHint. */
export interface CoachAIContext {
  level: CoachLevel | null;
  comms: CommsStyle;
  /** Cached users.coach_brain_rating, 0-100. Optional — null means
   *  no Brain eval yet, hint falls back to level-only behaviour. */
  brain_rating?: number | null;
}

export interface AICallOptions {
  system: string;
  userContent: string;
  maxTokens?: number;
  onStatus?: (msg: string) => void;
  /** AI surface id — routes the call through getModelFor() so the
   *  override panel can pin it independently. Falls back to getModel()
   *  when omitted. */
  surface?: SurfaceId;
  /** Explicit effort tier. When present, overrides the surface default
   *  and the ModelOverridePanel — `low` → Haiku, `medium` → Sonnet,
   *  `high` → Opus. Coach-facing control lives on the Scratch Area
   *  (EffortPicker). */
  effort?: Effort;
  /** When true, the proxy appends Anthropic's web_search server-tool
   *  to the request. The model decides when to call it; searches run
   *  entirely inside Anthropic + return inline citations. Caps at
   *  max_uses=3 per turn (~$0.03 worst-case). */
  enableWebSearch?: boolean;
  /** Coach context — when present, the helper appends a level-tuned
   *  style hint to the system prompt so the output tone matches the
   *  coach's level + Brain rating. Strictly additive — surfaces
   *  that don't pass it keep working unchanged. */
  coachContext?: CoachAIContext;
  /** AbortSignal — when triggered, the in-flight call resolves to
   *  null instead of throwing. UI surfaces wire a Cancel button to
   *  an AbortController and pass its signal here. */
  signal?: AbortSignal;
}

/** Compose the effective system prompt: caller's `system` plus an
 *  auto-injected coach style hint when `coachContext` is supplied.
 *  Exported so callers that pre-build their own prompts (e.g. unit
 *  tests, the Brain agent) can produce the same effective string. */
export function effectiveSystem(opts: Pick<AICallOptions, 'system' | 'coachContext'>): string {
  if (!opts.coachContext) return opts.system;
  const hint = buildCoachStyleHint(opts.coachContext);
  if (!hint) return opts.system;
  return `${opts.system}\n\n${hint}`;
}

/** Core request: forwards an Anthropic message body through the
 *  anthropic-proxy edge function. The proxy does its own retry for
 *  transient Anthropic errors, so this helper only retries on network
 *  + 5xx from the proxy itself. Returns the parsed Anthropic response. */
async function invokeProxy(body: Record<string, unknown>): Promise<any> {
  const startedAt = Date.now();
  const inputChars = estimateInputChars(body);
  const { data, error } = await supabase.functions.invoke('anthropic-proxy', { body });
  const durMs = Date.now() - startedAt;
  if (error) {
    // Supabase wraps proxy 4xx/5xx as a FunctionsHttpError; surface a
    // readable message, caller can log.
    const ctx = (error as any).context;
    let detail = '';
    try {
      if (ctx && typeof ctx.json === 'function') {
        const j = await ctx.json();
        detail = j?.error ?? JSON.stringify(j);
      }
    } catch { /* fall through */ }
    logAi.warn('proxy error', {
      model: body.model,
      inputChars,
      durMs,
      message: formatError(error),
      detail,
    });
    throw new Error(`anthropic-proxy: ${(error as any).message ?? 'error'}${detail ? ` — ${detail}` : ''}`);
  }
  const outputChars = estimateOutputChars(data);
  logAi.info('proxy round-trip', {
    model: body.model,
    maxTokens: body.max_tokens,
    inputChars,
    outputChars,
    durMs,
    stopReason: data?.stop_reason,
  });
  return data;
}

interface SupabaseStreamingClient {
  supabaseKey?: string;
  functionsUrl?: string;
  functions?: {
    url?: string;
    fetch?: typeof fetch;
  };
  auth?: {
    getSession?: () => Promise<{
      data?: {
        session?: {
          access_token?: string;
        } | null;
      };
    }>;
  };
}

interface SseEvent {
  event: string;
  data: unknown;
}

interface StreamedMessage {
  content: Array<Record<string, unknown>>;
  stop_reason?: string | null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function stringValue(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function numberValue(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

async function invokeProxyStream(
  body: Record<string, unknown>,
  onEvent: (event: SseEvent) => void,
): Promise<void> {
  const client = getSupabaseClient() as unknown as SupabaseStreamingClient;
  const functionsUrl = client.functions?.url ?? client.functionsUrl;
  const anonKey = client.supabaseKey;
  if (!functionsUrl || !anonKey) {
    throw new Error('anthropic-proxy streaming requires Supabase functions URL and anon key');
  }

  const session = await client.auth?.getSession?.();
  const accessToken = session?.data?.session?.access_token ?? anonKey;
  const response = await (client.functions?.fetch ?? fetch)(`${functionsUrl}/anthropic-proxy`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
      'apikey': anonKey,
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ ...body, stream: true }),
  });

  if (!response.ok) {
    throw new Error(`anthropic-proxy stream: ${response.status} ${await response.text()}`);
  }
  if (!response.body) {
    throw new Error('anthropic-proxy stream: empty response body');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const flushBlock = (block: string) => {
    const lines = block.split(/\r?\n/);
    let event = 'message';
    const dataLines: string[] = [];
    for (const line of lines) {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
    }
    const rawData = dataLines.join('\n').trim();
    if (!rawData || rawData === '[DONE]') return;
    try {
      onEvent({ event, data: JSON.parse(rawData) as unknown });
    } catch {
      onEvent({ event, data: rawData });
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const blocks = buffer.split(/\r?\n\r?\n/);
    buffer = blocks.pop() ?? '';
    for (const block of blocks) flushBlock(block);
  }

  buffer += decoder.decode();
  if (buffer.trim()) flushBlock(buffer);
}

async function anthropicRoundTripStream(
  bodyObj: Record<string, unknown>,
  onTextDelta?: (delta: string, fullText: string) => void,
): Promise<StreamedMessage> {
  const content: Array<Record<string, unknown>> = [];
  const toolJson = new Map<number, string>();
  let streamedText = '';
  let stopReason: string | null = null;

  await invokeProxyStream(bodyObj, ({ data }) => {
    const payload = asRecord(data);
    if (!payload) return;
    const eventType = stringValue(payload.type);

    if (eventType === 'content_block_start') {
      const index = numberValue(payload.index);
      const block = asRecord(payload.content_block);
      if (index === null || !block) return;
      if (block.type === 'tool_use') {
        content[index] = {
          type: 'tool_use',
          id: stringValue(block.id) ?? '',
          name: stringValue(block.name) ?? '',
          input: {},
        };
        toolJson.set(index, '');
      } else if (block.type === 'text') {
        content[index] = { type: 'text', text: stringValue(block.text) ?? '' };
      } else {
        content[index] = { ...block };
      }
      return;
    }

    if (eventType === 'content_block_delta') {
      const index = numberValue(payload.index);
      const delta = asRecord(payload.delta);
      if (index === null || !delta) return;
      const block = content[index] ?? { type: 'text', text: '' };
      content[index] = block;
      if (delta.type === 'text_delta') {
        const text = stringValue(delta.text) ?? '';
        block.text = `${stringValue(block.text) ?? ''}${text}`;
        streamedText += text;
        onTextDelta?.(text, streamedText);
      } else if (delta.type === 'input_json_delta') {
        toolJson.set(index, `${toolJson.get(index) ?? ''}${stringValue(delta.partial_json) ?? ''}`);
      }
      return;
    }

    if (eventType === 'content_block_stop') {
      const index = numberValue(payload.index);
      if (index === null) return;
      const block = content[index];
      if (block?.type !== 'tool_use') return;
      const raw = toolJson.get(index) ?? '';
      try {
        block.input = raw ? JSON.parse(raw) as unknown : {};
      } catch {
        block.input = {};
      }
      return;
    }

    if (eventType === 'message_delta') {
      const delta = asRecord(payload.delta);
      stopReason = stringValue(delta?.stop_reason) ?? stopReason;
    }
  });

  return { content: content.filter(Boolean), stop_reason: stopReason };
}

/** Rough character count of the outbound messages — used for a quick
 *  "how big was this call" signal in the debug panel without doing
 *  expensive token counting on every hop. */
function estimateInputChars(body: Record<string, unknown>): number {
  let n = 0;
  if (typeof body.system === 'string') n += body.system.length;
  const msgs = body.messages as Array<{ content: unknown }> | undefined;
  if (!Array.isArray(msgs)) return n;
  for (const m of msgs) {
    if (typeof m.content === 'string') n += m.content.length;
    else if (Array.isArray(m.content)) {
      for (const block of m.content) {
        const b = block as { type?: string; text?: string };
        if (b?.type === 'text' && typeof b.text === 'string') n += b.text.length;
        // images count as their ~base64 size — approximate only
        else if (b?.type === 'image') n += 10_000;
      }
    }
  }
  return n;
}

function estimateOutputChars(data: unknown): number {
  const blocks = (data as { content?: Array<{ type?: string; text?: string }> })?.content;
  if (!Array.isArray(blocks)) return 0;
  let n = 0;
  for (const b of blocks) {
    if (b?.type === 'text' && typeof b.text === 'string') n += b.text.length;
  }
  return n;
}

/**
 * Call Anthropic API and return the raw text response.
 * The edge function handles retries on transient upstream errors so
 * this helper just forwards + parses.
 *
 * Response shape note: when web_search (or any other server-tool) fires,
 * the response's content array is PREFIXED with server_tool_use +
 * tool_result blocks BEFORE the final text block. Old code that took
 * content[0].text silently returned null in that case. We now walk the
 * array for the first text block and concatenate any subsequent ones.
 */
export async function callAI(opts: AICallOptions): Promise<string | null> {
  const status = opts.onStatus ?? (() => {});
  const bodyObj: Record<string, unknown> = {
    model: pickModel(opts.surface, opts.effort),
    max_tokens: opts.maxTokens ?? 1500,
    system: effectiveSystem(opts),
    messages: [{ role: 'user', content: opts.userContent }],
  };
  if (opts.enableWebSearch) bodyObj.enable_web_search = true;

  if (opts.signal?.aborted) return null;
  status('Sending to AI...');
  let data: any;
  try {
    data = await invokeProxy(bodyObj);
  } catch (e: any) {
    if (opts.signal?.aborted) return null;
    throw e;
  }
  if (opts.signal?.aborted) return null;
  status('Parsing response...');
  const blocks = Array.isArray(data?.content) ? data.content : [];
  const texts: string[] = [];
  for (const b of blocks) {
    if (b?.type === 'text' && typeof b.text === 'string') texts.push(b.text);
  }
  if (texts.length === 0) return null;
  return texts.join('\n').trim() || null;
}

/**
 * Call AI and parse the JSON from the response.
 * Extracts the first {...} block from the response text.
 */
export interface AIVisionCallOptions {
  system: string;
  instruction: string; // text that accompanies the images
  images: { base64: string; mediaType: string }[];
  maxTokens?: number;
  onStatus?: (msg: string) => void;
  /** AI surface id (see aiSurfaces.ts) — used to resolve the model
   *  override + record a trace entry. */
  surface?: SurfaceId;
  /** Explicit effort tier; overrides surface default when set. */
  effort?: Effort;
}

/** Call Anthropic vision API with a sequence of images. */
export async function callVisionAI(opts: AIVisionCallOptions): Promise<string | null> {
  const status = opts.onStatus ?? (() => {});
  const content: any[] = [];
  for (const img of opts.images) {
    content.push({
      type: 'image',
      source: { type: 'base64', media_type: img.mediaType, data: img.base64 },
    });
  }
  content.push({ type: 'text', text: opts.instruction });

  const bodyObj = {
    model: pickModel(opts.surface, opts.effort),
    max_tokens: opts.maxTokens ?? 4096,
    system: opts.system,
    messages: [{ role: 'user', content }],
  };

  status(`Analysing ${opts.images.length} frames...`);
  const data = await invokeProxy(bodyObj);
  return data?.content?.[0]?.text ?? null;
}

/** Call vision and parse JSON from the response (with repair fallback). */
export async function callVisionAIJson<T>(opts: AIVisionCallOptions): Promise<T | null> {
  const text = await callVisionAI(opts);
  if (!text) return null;
  const match = text.match(/\{[\s\S]*\}/);
  if (!match) return null;
  try { return JSON.parse(match[0]) as T; }
  catch { return null; }
}

// ============================================================================
// Tool-use loop — used by the Coach Brain sidebar assistant.
// Claude's tool-use API: model returns content blocks with `tool_use` items;
// we execute via dispatcher, append `tool_result` blocks, re-send until
// stop_reason === 'end_turn' (or maxRounds exhausted).
// ============================================================================

export interface AnthropicTool {
  name: string;
  description: string;
  input_schema: {
    type: 'object';
    properties: Record<string, unknown>;
    required?: string[];
  };
}

export type ToolDispatcher = (name: string, input: unknown) => Promise<unknown>;

export interface ToolTraceEntry {
  name: string;
  input: unknown;
  output: unknown;
  errored?: boolean;
}

export interface AIMessage {
  role: 'user' | 'assistant';
  content: unknown;                        // string OR content-block array
}

export interface AIToolsCallOptions {
  system: string;
  messages: AIMessage[];
  tools: AnthropicTool[];
  maxRounds?: number;
  maxTokens?: number;
  dispatch: ToolDispatcher;
  onStatus?: (msg: string) => void;
  /** Called whenever the model decides to call a tool — lets UI show live trace. */
  onTool?: (entry: ToolTraceEntry) => void;
  /** AI surface id. Resolved once per tool-round so mid-conversation
   *  override flips pick up on the next round. */
  surface?: SurfaceId;
  /** Explicit effort tier; overrides surface default when set. */
  effort?: Effort;
  /** Opt into Anthropic's native web_search server-tool. Adds it to
   *  every round's tools array via the proxy. Coach-facing "browsing
   *  the web…" status surfaces through onTool when it fires. */
  enableWebSearch?: boolean;
}

export interface AIStreamingToolsCallOptions extends AIToolsCallOptions {
  /** Called for visible assistant text deltas as the final/tool-loop response streams. */
  onTextDelta?: (delta: string, fullText: string) => void;
}

export interface WebCitation {
  url: string;
  title: string;
  citedText?: string;
}

export interface AIToolsResult {
  finalText: string;
  toolTrace: ToolTraceEntry[];
  /**
   * Every draft the model produced during the turn, in call order. A single
   * turn can legitimately produce multiple drafts — e.g. "give me 5 attacking
   * principles" fans out into 5 draft_principle tool calls. UI should render
   * each as its own approval card.
   */
  drafts: Array<{ type: string; payload: unknown }>;
  /** Back-compat alias: the first draft in the turn, or undefined. */
  draft?: { type: string; payload: unknown };
  /** Full message list as extended by tool rounds (for persistence/debug). */
  messages: AIMessage[];
  /**
   * Web-search citations aggregated across all text blocks in the final
   * assistant turn. Only populated when `enableWebSearch` was true and the
   * model actually used the tool. Deduped by URL.
   */
  webCitations: WebCitation[];
}

/** Shared tool-use round trip. Returns the parsed Anthropic JSON body.
 *  The proxy handles retries on transient upstream errors. */
async function anthropicRoundTrip(bodyObj: Record<string, unknown>): Promise<any> {
  return invokeProxy(bodyObj);
}

/**
 * Tool-use loop. Returns the final assistant text plus a trace of tool calls.
 * A tool result of shape `{ kind: 'draft', type, payload }` terminates the loop
 * and is surfaced as `result.draft` so the UI can render a preview card.
 */
export async function callAITools(opts: AIToolsCallOptions): Promise<AIToolsResult> {
  const status = opts.onStatus ?? (() => {});
  const trace: ToolTraceEntry[] = [];
  const maxRounds = opts.maxRounds ?? 6;
  const maxTokens = opts.maxTokens ?? 2000;

  // Mutable conversation — we append tool rounds here.
  const messages: AIMessage[] = [...opts.messages];
  const drafts: AIToolsResult['drafts'] = [];

  for (let round = 0; round < maxRounds; round++) {
    status(round === 0 ? 'Thinking...' : `Reading data (round ${round + 1})...`);

    const roundBody: Record<string, unknown> = {
      model: pickModel(opts.surface, opts.effort),
      max_tokens: maxTokens,
      system: opts.system,
      tools: opts.tools,
      messages,
    };
    if (opts.enableWebSearch) roundBody.enable_web_search = true;
    const data = await anthropicRoundTrip(roundBody);

    const content = data?.content ?? [];
    // Record the assistant turn verbatim so the next request has proper context.
    messages.push({ role: 'assistant', content });

    const toolUses = content.filter((b: any) => b?.type === 'tool_use');
    logTools.debug(`round ${round + 1}`, {
      stopReason: data.stop_reason,
      toolCalls: toolUses.map((t: any) => t.name),
    });

    if (data.stop_reason === 'end_turn' || toolUses.length === 0) {
      const finalText = content
        .filter((b: any) => b?.type === 'text')
        .map((b: any) => b.text)
        .join('\n')
        .trim();
      // Collect web_search citations from every text block's `citations`
      // array. Deduped by URL so repeat references to the same source
      // collapse into one entry in the Sources list.
      const citationMap = new Map<string, WebCitation>();
      for (const block of content) {
        if ((block as any)?.type !== 'text') continue;
        const cites = (block as any).citations;
        if (!Array.isArray(cites)) continue;
        for (const c of cites) {
          if (!c || typeof c.url !== 'string') continue;
          if (citationMap.has(c.url)) continue;
          citationMap.set(c.url, {
            url: c.url,
            title: typeof c.title === 'string' && c.title ? c.title : c.url,
            citedText: typeof c.cited_text === 'string' ? c.cited_text : undefined,
          });
        }
      }
      const webCitations = Array.from(citationMap.values());
      return { finalText, toolTrace: trace, drafts, draft: drafts[0], messages, webCitations };
    }

    // Execute every requested tool and feed results back.
    const toolResults: any[] = [];
    for (const tu of toolUses) {
      const name = tu.name as string;
      const input = tu.input;
      status(`Running ${name}...`);
      let output: unknown;
      let errored = false;
      try {
        output = await opts.dispatch(name, input);
      } catch (err) {
        errored = true;
        output = { error: err instanceof Error ? err.message : String(err) };
        logTools.warn(`tool ${name} threw`, { message: formatError(err) });
      }
      const entry: ToolTraceEntry = { name, input, output, errored };
      trace.push(entry);
      opts.onTool?.(entry);

      // Collect every draft the model produces. One turn can produce several
      // (bulk principle generation, batch drill ideation, etc.) — each gets
      // its own approval card downstream.
      if (
        !errored &&
        output && typeof output === 'object' &&
        (output as any).kind === 'draft' &&
        typeof (output as any).type === 'string'
      ) {
        drafts.push({ type: (output as any).type, payload: (output as any).payload });
      }

      toolResults.push({
        type: 'tool_result',
        tool_use_id: tu.id,
        content: JSON.stringify(output).slice(0, 8000),   // hard cap to protect tokens
        is_error: errored,
      });
    }

    messages.push({ role: 'user', content: toolResults });

    // If we captured a draft, let the model write one short prose follow-up, then stop.
    // We do this by continuing the loop once more; the model typically ends its turn.
  }

  // maxRounds exhausted — return whatever text we have.
  status('Reached max tool rounds, returning.');
  const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant');
  const lastContent = Array.isArray(lastAssistant?.content) ? lastAssistant!.content : [];
  const finalText = (lastContent as any[])
    .filter(b => b?.type === 'text')
    .map(b => b.text)
    .join('\n')
    .trim();
  return { finalText, toolTrace: trace, drafts, draft: drafts[0], messages, webCitations: [] };
}

/**
 * Streaming variant of `callAITools`. The tool loop behavior and result shape
 * stay the same, but assistant text deltas are surfaced through `onTextDelta`
 * as Anthropic emits them.
 */
export async function callAIToolsStreaming(opts: AIStreamingToolsCallOptions): Promise<AIToolsResult> {
  const status = opts.onStatus ?? (() => {});
  const trace: ToolTraceEntry[] = [];
  const maxRounds = opts.maxRounds ?? 6;
  const maxTokens = opts.maxTokens ?? 2000;
  const messages: AIMessage[] = [...opts.messages];
  const drafts: AIToolsResult['drafts'] = [];

  for (let round = 0; round < maxRounds; round++) {
    status(round === 0 ? 'Thinking...' : `Reading data (round ${round + 1})...`);

    const roundBody: Record<string, unknown> = {
      model: pickModel(opts.surface, opts.effort),
      max_tokens: maxTokens,
      system: opts.system,
      tools: opts.tools,
      messages,
    };
    if (opts.enableWebSearch) roundBody.enable_web_search = true;

    const data = await anthropicRoundTripStream(roundBody, opts.onTextDelta);
    const content = data.content;
    messages.push({ role: 'assistant', content });

    const toolUses = content.filter((b) => b.type === 'tool_use');
    logTools.debug(`stream round ${round + 1}`, {
      stopReason: data.stop_reason,
      toolCalls: toolUses.map((t) => stringValue(t.name) ?? '<unknown>'),
    });

    if (data.stop_reason === 'end_turn' || toolUses.length === 0) {
      const finalText = content
        .filter((b) => b.type === 'text')
        .map((b) => stringValue(b.text) ?? '')
        .join('\n')
        .trim();
      return { finalText, toolTrace: trace, drafts, draft: drafts[0], messages, webCitations: [] };
    }

    const toolResults: Array<Record<string, unknown>> = [];
    for (const toolUse of toolUses) {
      const name = stringValue(toolUse.name) ?? '';
      const input = toolUse.input;
      status(`Running ${name}...`);
      let output: unknown;
      let errored = false;
      try {
        output = await opts.dispatch(name, input);
      } catch (err) {
        errored = true;
        output = { error: err instanceof Error ? err.message : String(err) };
        logTools.warn(`tool ${name} threw`, { message: formatError(err) });
      }

      const entry: ToolTraceEntry = { name, input, output, errored };
      trace.push(entry);
      opts.onTool?.(entry);

      if (
        !errored &&
        output &&
        typeof output === 'object' &&
        (output as { kind?: unknown }).kind === 'draft' &&
        typeof (output as { type?: unknown }).type === 'string'
      ) {
        const draftOutput = output as { type: string; payload: unknown };
        drafts.push({ type: draftOutput.type, payload: draftOutput.payload });
      }

      toolResults.push({
        type: 'tool_result',
        tool_use_id: stringValue(toolUse.id) ?? '',
        content: JSON.stringify(output).slice(0, 8000),
        is_error: errored,
      });
    }

    messages.push({ role: 'user', content: toolResults });
  }

  status('Reached max tool rounds, returning.');
  const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');
  const lastContent = Array.isArray(lastAssistant?.content)
    ? lastAssistant.content as Array<Record<string, unknown>>
    : [];
  const finalText = lastContent
    .filter((b) => b.type === 'text')
    .map((b) => stringValue(b.text) ?? '')
    .join('\n')
    .trim();
  return { finalText, toolTrace: trace, drafts, draft: drafts[0], messages, webCitations: [] };
}

export async function callAIJson<T>(opts: AICallOptions): Promise<T | null> {
  const text = await callAI(opts);
  opts.onStatus?.('Parsing response...');
  if (!text) {
    logJson.warn('no text returned from callAI', { surface: opts.surface });
    return null;
  }
  // Prefer a ```json ... ``` fenced block if present — handles the common
  // case where the model prefixes prose ("Let me formulate the response…")
  // before the actual JSON. Fall back to the greedy first-to-last-brace
  // regex when no fence is present.
  let jsonText: string | null = null;
  const fenceMatch = text.match(/```(?:json)?\s*\n?([\s\S]*?)\n?```/i);
  if (fenceMatch && fenceMatch[1]) {
    const inner = fenceMatch[1].trim();
    if (inner.startsWith('{') || inner.startsWith('[')) jsonText = inner;
  }
  if (!jsonText) {
    const match = text.match(/\{[\s\S]*\}/);
    if (match) jsonText = match[0];
  }
  if (!jsonText) {
    logJson.warn('no JSON object in response — model likely refused or replied in prose', {
      surface: opts.surface,
      textLen: text.length,
      headPreview: text.slice(0, 400),
      tailPreview: text.length > 400 ? text.slice(-200) : undefined,
    });
    return null;
  }
  // Rebind for the existing parse-try/repair block below.
  const match = [jsonText] as unknown as RegExpMatchArray;

  const json = match[0];
  try {
    return JSON.parse(json) as T;
  } catch (parseErr) {
    logJson.warn('parse failed — attempting repair', {
      rawLen: json.length,
      tail: json.slice(-500),
      message: formatError(parseErr),
    });

    // Bracket-stack walk: find unbalanced depth, then append closers.
    const stack: string[] = [];
    let inString = false;
    let escape = false;

    for (let i = 0; i < json.length; i++) {
      const ch = json[i];
      if (escape) { escape = false; continue; }
      if (ch === '\\') { escape = true; continue; }
      if (ch === '"') { inString = !inString; continue; }
      if (inString) continue;
      if (ch === '{') stack.push('}');
      else if (ch === '[') stack.push(']');
      else if (ch === '}' || ch === ']') {
        if (stack.length > 0) stack.pop();
      }
    }

    if (stack.length === 0) {
      logJson.error('repair abandoned — brackets balanced but invalid');
      return null;
    }

    // Trim trailing partial tokens so the close-bracket sequence lands
    // on a valid value boundary.
    let truncated = json;
    truncated = truncated.replace(/,\s*"[^"]*$/, '');                    // trailing partial key
    truncated = truncated.replace(/,\s*"[^"]*"\s*:\s*"?[^"}\]]*$/, '');  // trailing partial key:value
    truncated = truncated.replace(/,\s*\{[^}]*$/, '');                   // trailing partial object
    truncated = truncated.replace(/,\s*$/, '');                          // trailing comma

    // Recount in case the trim undid a nesting.
    const stack2: string[] = [];
    let inStr2 = false;
    let esc2 = false;
    for (let i = 0; i < truncated.length; i++) {
      const ch = truncated[i];
      if (esc2) { esc2 = false; continue; }
      if (ch === '\\') { esc2 = true; continue; }
      if (ch === '"') { inStr2 = !inStr2; continue; }
      if (inStr2) continue;
      if (ch === '{') stack2.push('}');
      else if (ch === '[') stack2.push(']');
      else if (ch === '}' || ch === ']') { if (stack2.length) stack2.pop(); }
    }

    const repaired = truncated + stack2.reverse().join('');
    try {
      const parsed = JSON.parse(repaired) as T;
      logJson.info('repair succeeded', { originalLen: json.length, repairedLen: repaired.length });
      return parsed;
    } catch (e2) {
      logJson.error('repair failed', {
        message: formatError(e2),
        repairedLen: repaired.length,
        tail: repaired.slice(-500),
      });
      return null;
    }
  }
}
